import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  ArrowLeft, Edit, Image as ImageIcon, Link as LinkIcon, Layers,
  Search, Palette, Info,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import TipTapEditor from "./TipTapEditor";
import ProjectImageField from "./ProjectImageField";
import ProjectLinksInput from "./ProjectLinksInput";
import ProjectGalleryInput from "./ProjectGalleryInput";
import setAuthToken from "../../utils/setAuthToken";
import { parseProjectLinks } from "../../utils/projectLinks";

import {
  PageHeader,
  Card,
  Input,
  Textarea,
  Select,
  Switch,
  NumberInput,
  FormField,
  TagInput,
  Button,
  Spinner,
  ProjectCover,
} from "../../design-system";

const notify = (title, message, type = "success", duration = 3000) =>
  Store.addNotification({
    title,
    message,
    type,
    insert: "top",
    container: "top-right",
    dismiss: { duration },
  });

/** Le champ TipTap arrive tantôt en JSON stringifié, tantôt en objet. */
const parseContent = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "null") return "";

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return "";
  }
};

const toArray = (value) => (Array.isArray(value) ? value : []);

class ProjectForm extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props.match.params;

    this.state = {
      id: id || null,
      title: "",
      slug: "",
      tagline: "",
      excerpt: "",
      content: "",
      status: "DRAFT",
      featured: false,
      order: 0,
      coverUrl: "",
      thumbUrl: "",
      accentColor: "",
      gallery: [],
      role: "",
      period: "",
      clientName: "",
      technologies: [],
      frameworks: [],
      databases: [],
      tools: [],
      links: [],
      metaTitle: "",
      metaDescription: "",
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    this.withAuth();
    if (this.state.id) this.loadProject();
  }

  withAuth = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
  };

  loadProject = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get(`/api/projects/id/${this.state.id}`);
      const project = response.data.project;

      this.setState({
        title: project.title || "",
        slug: project.slug || "",
        tagline: project.tagline || "",
        excerpt: project.excerpt || "",
        content: parseContent(project.content),
        status: project.status || "DRAFT",
        featured: Boolean(project.featured),
        order: project.order || 0,
        coverUrl: project.coverUrl || "",
        thumbUrl: project.thumbUrl || "",
        accentColor: project.accentColor || "",
        gallery: toArray(project.gallery),
        role: project.role || "",
        period: project.period || "",
        clientName: project.clientName || "",
        technologies: toArray(project.technologies),
        frameworks: toArray(project.frameworks),
        databases: toArray(project.databases),
        tools: toArray(project.tools),
        links: parseProjectLinks(project.links),
        metaTitle: project.metaTitle || "",
        metaDescription: project.metaDescription || "",
        loading: false,
      });
    } catch (error) {
      console.error("Error loading project:", error);
      this.setState({ loading: false });
      notify("Erreur", "Impossible de charger le projet", "danger");
    }
  };

  generateSlug = (title) =>
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  handleTitleChange = (e) => {
    const title = e.target.value;
    // Le slug suit le titre tant qu'il n'a pas été édité à la main.
    const slugFollowsTitle = !this.state.slug || this.state.slug === this.generateSlug(this.state.title);
    this.setState({ title, ...(slugFollowsTitle ? { slug: this.generateSlug(title) } : {}) });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true });

    try {
      this.withAuth();

      const projectData = {
        title: this.state.title,
        slug: this.state.slug,
        tagline: this.state.tagline,
        excerpt: this.state.excerpt,
        // L'API se charge du JSON.stringify (même convention que les articles).
        content: this.state.content || null,
        status: this.state.status,
        featured: this.state.featured,
        order: parseInt(this.state.order) || 0,
        coverUrl: this.state.coverUrl,
        thumbUrl: this.state.thumbUrl,
        accentColor: this.state.accentColor,
        gallery: this.state.gallery,
        role: this.state.role,
        period: this.state.period,
        clientName: this.state.clientName,
        technologies: this.state.technologies,
        frameworks: this.state.frameworks,
        databases: this.state.databases,
        tools: this.state.tools,
        links: this.state.links,
        metaTitle: this.state.metaTitle,
        metaDescription: this.state.metaDescription,
      };

      if (this.state.id) await axios.put(`/api/projects/${this.state.id}`, projectData);
      else                await axios.post("/api/projects", projectData);

      notify(
        `Projet ${this.state.id ? "modifié" : "créé"}`,
        `« ${this.state.title} » a été enregistré.`,
      );
      this.props.history.push("/dashboard/projects");
    } catch (error) {
      console.error("Error saving project:", error);
      notify("Erreur", error.response?.data?.message || "Erreur lors de la sauvegarde", "danger", 5000);
      this.setState({ saving: false });
    }
  };

  render() {
    const { loading, saving } = this.state;
    const isEdit = !!this.state.id;
    const pageTitle = isEdit ? "Modifier le projet" : "Nouveau projet";

    if (loading) {
      return (
        <AdminPageShell pageTitle={pageTitle}>
          <div className="flex items-center justify-center py-20 text-mute">
            <Spinner size="lg" />
          </div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell
        pageTitle={pageTitle}
        breadcrumbLast={isEdit ? this.state.title || "Modifier" : undefined}
      >
        <PageHeader
          eyebrow="— Projets"
          title={pageTitle}
          description={
            isEdit
              ? "Mets à jour la fiche et la page publique de ce projet."
              : "Décris le projet : contexte, stack, captures et liens."
          }
          actions={
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              onClick={() => this.props.history.push("/dashboard/projects")}
            >
              Retour
            </Button>
          }
        />

        <form onSubmit={this.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* ---------------------------------------------------------- */}
            {/*  Colonne principale                                        */}
            {/* ---------------------------------------------------------- */}
            <div className="lg:col-span-2 space-y-5">
              <Card>
                <Card.Header>
                  <Card.Title>Informations principales</Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Nom du projet" required>
                    <Input value={this.state.title} onChange={this.handleTitleChange} required autoFocus />
                  </FormField>

                  <FormField
                    label="Slug"
                    required
                    helper={`URL publique : /projets/${this.state.slug || "…"}`}
                  >
                    <Input
                      value={this.state.slug}
                      onChange={(e) => this.setState({ slug: e.target.value })}
                      pattern="[a-z0-9-]+"
                      required
                    />
                  </FormField>

                  <FormField
                    label="Accroche"
                    helper="Une ligne, affichée sous le titre sur les cartes et le hero."
                  >
                    <Input
                      value={this.state.tagline}
                      onChange={(e) => this.setState({ tagline: e.target.value })}
                      placeholder="Ce que fait le projet, en une phrase."
                    />
                  </FormField>

                  <FormField label="Résumé" helper="2 à 3 phrases pour la liste et les partages.">
                    <Textarea
                      rows={3}
                      value={this.state.excerpt}
                      onChange={(e) => this.setState({ excerpt: e.target.value })}
                    />
                  </FormField>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField label="Rôle" helper="Ex. Dev fullstack.">
                      <Input
                        value={this.state.role}
                        onChange={(e) => this.setState({ role: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Période" helper="Ex. 2024 — en cours.">
                      <Input
                        value={this.state.period}
                        onChange={(e) => this.setState({ period: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Client" helper="Vide si projet perso.">
                      <Input
                        value={this.state.clientName}
                        onChange={(e) => this.setState({ clientName: e.target.value })}
                      />
                    </FormField>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <Edit className="w-4 h-4 text-text" strokeWidth={1.8} />
                  <h3 className="text-sm font-bold tracking-tight">Le récit du projet</h3>
                </div>
                <div className="editor-container">
                  <TipTapEditor
                    content={this.state.content}
                    onChange={(content) => this.setState({ content })}
                    placeholder="Le contexte, le problème, les choix techniques, ce qui a coincé…"
                  />
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>
                    <span className="inline-flex items-center gap-2">
                      <Layers className="w-4 h-4 text-mute" strokeWidth={1.8} />
                      Stack technique
                    </span>
                  </Card.Title>
                  <Card.Description>
                    Séparer avec Entrée ou virgule. Chaque groupe s'affiche en puces sur la page.
                  </Card.Description>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Langages">
                    <TagInput
                      value={this.state.technologies}
                      onChange={(technologies) => this.setState({ technologies })}
                      placeholder="JavaScript, Rust, Java…"
                    />
                  </FormField>
                  <FormField label="Frameworks">
                    <TagInput
                      value={this.state.frameworks}
                      onChange={(frameworks) => this.setState({ frameworks })}
                      placeholder="React, Express, Tauri…"
                    />
                  </FormField>
                  <FormField label="Bases de données">
                    <TagInput
                      value={this.state.databases}
                      onChange={(databases) => this.setState({ databases })}
                      placeholder="PostgreSQL, Redis…"
                    />
                  </FormField>
                  <FormField label="Librairies et outils">
                    <TagInput
                      value={this.state.tools}
                      onChange={(tools) => this.setState({ tools })}
                      placeholder="Prisma, TipTap, Docker…"
                    />
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>
                    <span className="inline-flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-mute" strokeWidth={1.8} />
                      Liens
                    </span>
                  </Card.Title>
                  <Card.Description>Dépôt, démo en ligne, documentation…</Card.Description>
                </Card.Header>
                <ProjectLinksInput
                  value={this.state.links}
                  onChange={(links) => this.setState({ links })}
                />
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>
                    <span className="inline-flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-mute" strokeWidth={1.8} />
                      Galerie
                    </span>
                  </Card.Title>
                  <Card.Description>Captures d'écran affichées sur la page du projet.</Card.Description>
                </Card.Header>
                <ProjectGalleryInput
                  value={this.state.gallery}
                  onChange={(gallery) => this.setState({ gallery })}
                  slug={this.state.slug}
                />
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>
                    <span className="inline-flex items-center gap-2">
                      <Search className="w-4 h-4 text-mute" strokeWidth={1.8} />
                      Référencement
                    </span>
                  </Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField label="Titre SEO" helper="Idéalement 50–60 caractères.">
                    <Input
                      value={this.state.metaTitle}
                      onChange={(e) => this.setState({ metaTitle: e.target.value })}
                      placeholder={this.state.title || "Titre pour les moteurs de recherche"}
                    />
                  </FormField>
                  <FormField label="Description SEO" helper="Idéalement 120–155 caractères.">
                    <Textarea
                      rows={3}
                      value={this.state.metaDescription}
                      onChange={(e) => this.setState({ metaDescription: e.target.value })}
                      placeholder={this.state.excerpt || "Résumé affiché dans les résultats de recherche"}
                    />
                  </FormField>
                </div>
              </Card>
            </div>

            {/* ---------------------------------------------------------- */}
            {/*  Colonne latérale                                          */}
            {/* ---------------------------------------------------------- */}
            <div className="space-y-5">
              <Card variant="filled">
                <div className="space-y-2">
                  <Button type="submit" fullWidth isLoading={saving}>
                    {isEdit ? "Enregistrer les modifications" : "Créer le projet"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => this.props.history.push("/dashboard/projects")}
                  >
                    Annuler
                  </Button>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>Publication</Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <FormField
                    label="Statut"
                    helper="Un brouillon reste invisible du public."
                  >
                    <Select
                      value={this.state.status}
                      onChange={(e) => this.setState({ status: e.target.value })}
                    >
                      <option value="DRAFT">Brouillon</option>
                      <option value="PUBLISHED">Publié</option>
                      <option value="ARCHIVED">Archivé</option>
                    </Select>
                  </FormField>

                  <Switch
                    label="Mettre en avant"
                    description="Apparaît dans le carrousel de l'accueil."
                    checked={this.state.featured}
                    onChange={(e) => this.setState({ featured: e.target.checked })}
                  />

                  <FormField label="Ordre d'affichage" helper="0 = premier.">
                    <NumberInput
                      value={Number(this.state.order) || 0}
                      onChange={(value) => this.setState({ order: value })}
                      min={0}
                    />
                  </FormField>
                </div>
              </Card>

              <Card>
                <Card.Header>
                  <Card.Title>
                    <span className="inline-flex items-center gap-2">
                      <Palette className="w-4 h-4 text-mute" strokeWidth={1.8} />
                      Visuel
                    </span>
                  </Card.Title>
                </Card.Header>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10.5px] font-mono uppercase tracking-wider text-mute mb-2">
                      Aperçu de la couverture
                    </p>
                    <ProjectCover
                      title={this.state.title || "Projet"}
                      slug={this.state.slug}
                      src={this.state.coverUrl}
                      accentColor={this.state.accentColor}
                      ratio="card"
                    />
                  </div>

                  <FormField
                    label="Image de couverture"
                    helper="Utilisée en fond du hero. Sans image, un dégradé est généré."
                  >
                    <ProjectImageField
                      value={this.state.coverUrl}
                      onChange={(coverUrl) => this.setState({ coverUrl })}
                      slug={this.state.slug}
                      label="Couverture"
                    />
                  </FormField>

                  <FormField
                    label="Vignette"
                    helper="Optionnelle — la couverture est utilisée par défaut."
                  >
                    <ProjectImageField
                      value={this.state.thumbUrl}
                      onChange={(thumbUrl) => this.setState({ thumbUrl })}
                      slug={this.state.slug}
                      label="Vignette"
                      compact
                    />
                  </FormField>

                  <FormField
                    label="Couleur d'accent"
                    helper="Pilote le dégradé de repli. Vide = teinte dérivée du slug."
                  >
                    <div className="flex gap-2">
                      <input
                        type="color"
                        aria-label="Choisir la couleur d'accent"
                        value={this.state.accentColor || "#0f1936"}
                        onChange={(e) => this.setState({ accentColor: e.target.value })}
                        className="h-10 w-12 shrink-0 rounded-md border border-border bg-surface cursor-pointer"
                      />
                      <Input
                        value={this.state.accentColor}
                        onChange={(e) => this.setState({ accentColor: e.target.value })}
                        placeholder="#0f1936"
                        aria-label="Couleur d'accent (hex)"
                      />
                      {this.state.accentColor && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => this.setState({ accentColor: "" })}
                        >
                          Auto
                        </Button>
                      )}
                    </div>
                  </FormField>
                </div>
              </Card>

              <Card variant="filled">
                <div className="flex gap-2.5 text-xs text-mute leading-relaxed">
                  <Info className="w-4 h-4 shrink-0 mt-px" strokeWidth={1.8} />
                  <p>
                    Un projet sans image n'est pas un projet sans visuel : la couverture
                    de repli est générée à partir de la couleur d'accent, ou du slug si
                    elle est vide.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(ProjectForm);
